/**
 * EntityBadgeGroup — multi-entity UI primitive (no store, no fetch).
 *
 * Renders a row of entity badges with overflow handling. Mirrors the
 * single `EntityBadge`'s state model but adds a `partial` state for the
 * "some ids resolved, others failed" case the wrappers can produce.
 *
 * The component is purely presentational: callers pass a fully-resolved
 * state and an optional per-id `onClick`. Wrappers add store hydration
 * and slideout-vs-page click translation on top.
 *
 * See `./types.ts` for the prop contract and issue #5032 for the
 * consolidation rationale.
 */

import { useCallback, useMemo, useState } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { cx } from '@/utils/cx';

import { EntityBadge } from './EntityBadge';
import { HoverPreviewBadge } from './HoverPreviewBadge';
import type {
  EntityBadgeData,
  EntityBadgeGroupState,
  EntityBadgeGroupVariant,
} from './types';

export interface EntityBadgeGroupProps {
  /** What to render. Discriminated union — see `EntityBadgeGroupState`. */
  state: EntityBadgeGroupState;
  /**
   * When supplied alongside resolved badge data, each child badge is
   * wrapped in `EntityBadgeWithHoverPreview` so hovering reveals the rich
   * (contact) or mini (everything else) preview. When `undefined`, child
   * badges render as bare `EntityBadge` — preserves backward compatibility
   * for legacy callers without org context.
   */
  orgId?: number;
  /**
   * Visual variant applied to every child badge. Defaults to `default`.
   *
   * `record` is excluded by type rather than given entries in the pill maps
   * below: it means "this is the record the row is about", and a row is about
   * exactly one record — a group of them is a category error, not a layout
   * this component should be able to draw. Excluding it here turns
   * `variant='record'` on a group into a compile error instead of a
   * plausible-looking wall of 24px avatars.
   */
  variant?: EntityBadgeGroupVariant;
  /** Pass through to each child badge — see `EntityBadge#avatar`. */
  avatar?: boolean;
  /**
   * Disables every child badge. Renders them as visually de-emphasized
   * (opacity-reduced) static spans. Use for "you can't do this right
   * now" read-only contexts.
   */
  disabled?: boolean;
  /**
   * When `false`, every child badge renders as a static span without
   * cursor / hover / focus affordances even if `onClick` is supplied.
   * Unlike `disabled`, this does NOT apply any visual de-emphasis —
   * the badges look identical to their clickable counterparts, just
   * without the click state. The overflow expand pill also becomes
   * non-interactive. Defaults to `true`.
   */
  interactive?: boolean;
  /**
   * Click handler invoked with the clicked entity's id. When omitted
   * (or when `disabled` / `interactive=false`), all child badges render
   * as static spans.
   */
  onClick?: (id: string) => void;
  /**
   * Maximum number of badges to render before collapsing the rest into
   * a `+N` overflow pill. Defaults to 3, matching the legacy group.
   */
  maxVisible?: number;
  /**
   * When `true` (default), clicking the `+N` overflow pill expands the
   * row to render every loaded entity. When `false`, the pill is static
   * — useful for read-only contexts or rendering surfaces that handle
   * expansion themselves (e.g. opening a slideout listing the rest).
   */
  expandable?: boolean;
  /**
   * Loading skeleton count. Used only when state is `loading`.
   * Defaults to 1, capped at `maxVisible`.
   */
  loadingCount?: number;
  /**
   * When `false`, the row never wraps — children render on a single line
   * and the badge text truncates with ellipsis if there isn't enough room.
   * Use this in fixed-height surfaces (e.g. CRM list cells) where wrapping
   * would inflate row height. Defaults to `true` (current behavior:
   * `flex-wrap` so all badges remain fully readable).
   */
  wrap?: boolean;
  /**
   * Pass-through to each child `EntityBadge` — see `EntityBadge#showEmail`.
   * When true, every child badge that has `email` populated on its data
   * renders `Name (email)`. Defaults to false.
   */
  showEmail?: boolean;
  /** Additional className applied to the outer flex container. */
  className?: string;
}

const DEFAULT_MAX_VISIBLE = 3;
const DEFAULT_LOADING_COUNT = 1;

/** Per-variant size for the overflow / failed pills so they match the badges. */
const PILL_SIZE_BY_VARIANT: Record<
  EntityBadgeGroupVariant,
  'sm' | 'md' | 'lg'
> = {
  default: 'md',
  inline: 'sm',
  xs: 'sm',
};

const PILL_PADDING_BY_VARIANT: Record<EntityBadgeGroupVariant, string> = {
  default: '!py-0.5 !px-2',
  inline: '!py-0 !px-1.5 align-middle text-xs',
  xs: '!py-0 !px-1.5 text-xs',
};

export function EntityBadgeGroup({
  state,
  orgId,
  variant = 'default',
  avatar = false,
  disabled = false,
  interactive = true,
  onClick,
  maxVisible = DEFAULT_MAX_VISIBLE,
  expandable = true,
  loadingCount = DEFAULT_LOADING_COUNT,
  wrap = true,
  showEmail = false,
  className,
}: EntityBadgeGroupProps): React.ReactElement | null {
  // Local expansion state — purely visual, no callbacks bubble out.
  // Reset implicitly when state changes (component re-mounts on data
  // identity changes via React's reconciliation).
  const [isExpanded, setIsExpanded] = useState(false);

  // Stable per-id click handler so child badges' callbacks don't change identity each render.
  // Note: child `EntityBadge`s also gate on their own `interactive`/`disabled` props,
  // so we propagate them rather than nullifying onClick here.
  const makeBadgeOnClick = useCallback(
    (id: string) => {
      if (!onClick || disabled || !interactive) {
        return undefined;
      }
      return () => onClick(id);
    },
    [onClick, disabled, interactive]
  );

  const handleExpand = useCallback(() => {
    setIsExpanded(true);
  }, []);

  const handleExpandKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        setIsExpanded(true);
      }
    },
    []
  );

  // Memoise visible/overflow slicing so re-renders don't re-allocate when state is stable.
  // When expanded, render every loaded entity and drop the overflow pill.
  const slice = useMemo(() => {
    const effectiveMax = isExpanded ? Number.POSITIVE_INFINITY : maxVisible;
    if (state.kind === 'data') {
      return {
        visible: isExpanded ? state.data : state.data.slice(0, effectiveMax),
        overflow: isExpanded
          ? 0
          : Math.max(0, state.data.length - effectiveMax),
      };
    }
    if (state.kind === 'partial') {
      return {
        visible: isExpanded
          ? state.loaded
          : state.loaded.slice(0, effectiveMax),
        overflow: isExpanded
          ? 0
          : Math.max(0, state.loaded.length - effectiveMax),
      };
    }
    return { visible: [] as EntityBadgeData[], overflow: 0 };
  }, [state, maxVisible, isExpanded]);

  const containerClass = cx(
    'flex items-center gap-1 min-w-0',
    wrap ? 'flex-wrap' : 'flex-nowrap',
    variant === 'inline' && 'align-middle',
    className
  );

  // Loading: render skeleton placeholders so list height stays stable
  // while the wrappers hydrate.
  if (state.kind === 'loading') {
    const count = Math.min(Math.max(1, loadingCount), maxVisible);
    return (
      <div
        className={containerClass}
        data-testid='entity-badge-group-loading'
        data-component='entity-badge-group'
        data-state='loading'
        aria-busy
      >
        {Array.from({ length: count }, (_, index) => (
          <EntityBadge
            key={`loading-${index}`}
            state={{ kind: 'loading' }}
            variant={variant}
          />
        ))}
      </div>
    );
  }

  // Total failure: single failed pill so the row doesn't collapse to nothing.
  if (state.kind === 'failed') {
    return (
      <div
        className={containerClass}
        data-testid='entity-badge-group-failed'
        data-component='entity-badge-group'
        data-state='failed'
      >
        <EntityBadge
          state={{ kind: 'failed' }}
          variant={variant}
          interactive={interactive}
          disabled={disabled}
        />
      </div>
    );
  }

  // Data + partial: render visible badges, then optional overflow + failed pills.
  // Empty data → null so callers don't render an empty container.
  if (state.kind === 'data' && state.data.length === 0) {
    return null;
  }
  if (
    state.kind === 'partial' &&
    state.loaded.length === 0 &&
    state.failedCount === 0
  ) {
    return null;
  }

  const failedCount = state.kind === 'partial' ? state.failedCount : 0;
  // Overflow pill is clickable only when expansion is allowed AND the
  // group is interactive AND not disabled. Otherwise it renders as a
  // static `+N` pill matching the legacy `CrmObjectBadgeGroup`.
  const overflowExpandable =
    slice.overflow > 0 && expandable && interactive && !disabled;

  return (
    <div
      className={containerClass}
      data-component='entity-badge-group'
      data-state={failedCount > 0 ? 'partial-failed' : 'loaded'}
    >
      {slice.visible.map(item => (
        <HoverPreviewBadge
          key={item.key ?? item.id}
          entityId={orgId !== undefined ? item.id : undefined}
          orgId={orgId}
          entityType={item.entityType}
          state={{ kind: 'data', data: item }}
          variant={variant}
          avatar={avatar}
          disabled={disabled}
          interactive={interactive}
          showEmail={showEmail}
          onClick={makeBadgeOnClick(item.id)}
        />
      ))}
      {slice.overflow > 0 &&
        (overflowExpandable ? (
          <button
            type='button'
            onClick={handleExpand}
            onKeyDown={handleExpandKeyDown}
            aria-label={`Show ${slice.overflow} more`}
            data-testid='entity-badge-group-expand'
            data-component='entity-badge-group-expand'
            className={cx(
              'inline-flex cursor-pointer rounded-full shrink-0',
              'hover:opacity-80 transition-opacity',
              'focus:outline-hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
            )}
          >
            <Badge
              type='pill-color'
              color='gray'
              size={PILL_SIZE_BY_VARIANT[variant]}
              className={cx(PILL_PADDING_BY_VARIANT[variant])}
            >
              +{slice.overflow}
            </Badge>
          </button>
        ) : (
          <Badge
            type='pill-color'
            color='gray'
            size={PILL_SIZE_BY_VARIANT[variant]}
            className={cx('shrink-0', PILL_PADDING_BY_VARIANT[variant])}
          >
            +{slice.overflow}
          </Badge>
        ))}
      {failedCount > 0 && (
        <Badge
          type='pill-color'
          color='error'
          size={PILL_SIZE_BY_VARIANT[variant]}
          className={cx('shrink-0', PILL_PADDING_BY_VARIANT[variant])}
        >
          <span
            data-testid='entity-badge-group-partial-failed'
            aria-label={`${failedCount} object${failedCount === 1 ? '' : 's'} failed to load`}
          >
            +{failedCount} failed
          </span>
        </Badge>
      )}
    </div>
  );
}
